import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { api, ApiError } from '@/services/api'
import { onboardingLog } from '@/services/onboardingLog'
import type { WelcomeBonusResponse } from '@/types/api'

export const useWelcomeBonusStore = defineStore('welcomeBonus', () => {
  const offer = ref<WelcomeBonusResponse | null>(null)
  const error = ref('')
  const loading = ref(false)
  const remainingSeconds = ref(0)
  let owner: string | null = null
  let revision = 0
  let expiresIn = 0
  let synchronizedAt = 0
  let pending: Promise<void> | undefined

  const active = computed(() => offer.value?.status === 'active' && remainingSeconds.value > 0)
  const countdown = computed(() => {
    const seconds = remainingSeconds.value
    return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`
  })

  function tick() {
    // Count from server time with a monotonic clock. Changing the device clock cannot reset an offer.
    remainingSeconds.value = Math.max(0, Math.ceil((expiresIn - (performance.now() - synchronizedAt)) / 1000))
  }

  function reset(userId: string | null = null) {
    revision++
    owner = userId
    pending = undefined
    offer.value = null
    error.value = ''
    loading.value = false
    remainingSeconds.value = 0
    expiresIn = 0
  }

  async function load(start = false) {
    if (!owner) return
    if (pending) return pending
    const version = revision
    loading.value = true
    error.value = ''
    pending = (async () => {
      try {
        const result = await (start ? api.startWelcomeBonus() : api.welcomeBonus())
        if (version !== revision) return
        offer.value = result
        expiresIn = result.status === 'active' && result.expires_at
          ? Math.max(0, Date.parse(result.expires_at) - Date.parse(result.server_now)) : 0
        synchronizedAt = performance.now()
        tick()
        onboardingLog('debug', '[FIX:UNI-116] welcome_bonus_synced', { status: result.status, granted: result.credits_granted })
      } catch (caught) {
        if (version !== revision) return
        error.value = caught instanceof ApiError ? caught.message : 'Could not load your offer. Please try again.'
        onboardingLog('warn', '[FIX:UNI-116] welcome_bonus_sync_failed', { status: caught instanceof ApiError ? caught.status : 'network' })
      } finally {
        if (version === revision) {
          loading.value = false
          pending = undefined
        }
      }
    })()
    return pending
  }

  async function enter(userId: string) {
    if (owner !== userId) reset(userId)
    if (!offer.value) await load(true)
  }

  return { offer, error, loading, active, countdown, remainingSeconds, tick, reset, enter, refresh: load }
})
